import React, { useEffect, useState } from "react";
import {
  getClientes,
  postRelacionClienteOrganismoTipoDocumento,
} from "../../componentes/http/actions/ClientesAction";
import { getOrganismoEmisorHabilitado } from "../../componentes/http/actions/OrganismoEmisorAction";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Grid,
  Typography,
  Button,
  Box,
  Snackbar,
  Alert,
  TablePagination,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  AccountTree as AccountTreeIcon,
  Description as DocumentIcon,
  Visibility as VisibilityIcon,
  Settings as SettingsIcon
} from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import FilterComponent from "../../componentes/data/FilterComponent";
import AgregarRelacionClienteOrganismoModal from "../../componentes/modal/Clientes/AgregarRelacionClienteOrganismoModal";

const GestionOrganismos = () => {
  const [clientes, setClientes] = useState([]);
  const [organismosConTipos, setOrganismosConTipos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterHabilitado, setFilterHabilitado] = useState("todos");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Estados para el modal de detalle
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedClienteDetail, setSelectedClienteDetail] = useState(null);

  useEffect(() => {
    fetchClientes();
    fetchOrganismosConTipos();
  }, []);

  const fetchClientes = () => {
    setIsLoading(true);
    getClientes(localStorage.getItem("token"))
      .then((response) => {
        setClientes(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.log(error);
        setClientes([]);
        setSnackbar({
          open: true,
          message: "Error al cargar clientes",
          severity: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
        setInitialDataLoaded(true);
      });
  };

  const fetchOrganismosConTipos = () => {
    getOrganismoEmisorHabilitado(localStorage.getItem("token"))
      .then((response) => {
        setOrganismosConTipos(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.log(error);
        setOrganismosConTipos([]);
      });
  };

  const filteredClientes = clientes
    .filter((cliente) => {
      const matchesSearch = cliente.nombre
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesFilter =
        filterHabilitado === "todos" ||
        (filterHabilitado === "habilitado" && cliente.habilitado === true) ||
        (filterHabilitado === "deshabilitado" && cliente.habilitado === false);
      return matchesSearch && matchesFilter;
    })
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPage(0);
  };
  
  const handleFilterChange = (newFilter) => {
    setFilterHabilitado(newFilter);
    setPage(0);
  };

  const filterOptions = [
    { value: "todos", label: "Todos" },
    { value: "habilitado", label: "Habilitados" },
    { value: "deshabilitado", label: "Deshabilitados" },
  ];

  const handleGuardarRelaciones = async (relaciones) => {
    try {
      setIsLoading(true);
      
      await postRelacionClienteOrganismoTipoDocumento(localStorage.getItem("token"), relaciones);

      setSnackbar({
        open: true,
        message: "Relaciones guardadas con éxito",
        severity: "success",
      });
      
      fetchClientes();
      setOpenModal(false);
    } catch (error) {
      console.error("Error al guardar relaciones:", error);
      setSnackbar({
        open: true,
        message: "Error al guardar las relaciones",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderOrganismosAsociados = (cliente) => {
    if (!cliente.relacionesOrganismoTipo || cliente.relacionesOrganismoTipo.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Sin organismos asociados
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {cliente.relacionesOrganismoTipo.map((relacion, idx) => (
          <Box key={idx}>
            <Chip 
              label={relacion.nombreOrganismo}
              variant="outlined"
              size="small"
              sx={{ mb: 0.5 }}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const renderTiposDocumento = (cliente) => {
    if (!cliente.relacionesOrganismoTipo || cliente.relacionesOrganismoTipo.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Sin tipos asociados
        </Typography>
      );
    }

    const getTotalTipos = () => {
      return cliente.relacionesOrganismoTipo.reduce((total, relacion) => 
        total + relacion.tipoDocumento.length, 0
      );
    };

    const getTotalOrganismos = () => cliente.relacionesOrganismoTipo.length;

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        p: 1,
      }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
            {getTotalOrganismos()} organismos
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {getTotalTipos()} tipos total
          </Typography>
        </Box>
      </Box>
    );
  };

  const DetailModal = () => (
    <Dialog 
      open={detailModalOpen} 
      onClose={() => setDetailModalOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 5, position: 'relative' }}>
          <AccountTreeIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">
              Organismos y Tipos de Documento
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              Cliente: {selectedClienteDetail?.nombre}
            </Typography>
          </Box>
          
          <IconButton
            aria-label="close"
            onClick={() => setDetailModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {selectedClienteDetail?.relacionesOrganismoTipo?.map((relacion, idx) => (
            <Paper key={idx} sx={{ p: 2, mb: 2 }} elevation={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" color="primary" sx={{ flex: 1 }}>
                  {relacion.nombreOrganismo}
                </Typography>
              </Box>
              
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Tipos de documento asociados ({relacion.tipoDocumento.length}):
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {relacion.tipoDocumento.map((tipo, tipoIdx) => (
                  <Chip
                    key={tipoIdx}
                    icon={<DocumentIcon />}
                    label={tipo}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Box>
              
              {relacion.tipoDocumento.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No hay tipos de documento asociados
                </Typography>
              )}
            </Paper>
          ))}
          
          {(!selectedClienteDetail?.relacionesOrganismoTipo || selectedClienteDetail?.relacionesOrganismoTipo.length === 0) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccountTreeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Sin organismos asociados
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Este cliente no tiene organismos emisores configurados
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => setDetailModalOpen(false)} 
          color="primary"
          sx={{ textTransform: 'none' }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Grid container direction="column" spacing={2} sx={{paddingTop: {xs: '20px', sm: '10px'}}}>
      <Grid item>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Gestión de Organismos y Tipos de Documento</Typography>
        </Grid>
        <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
          Administre la relación entre clientes, organismos emisores y tipos de documento
        </Typography>
      </Grid>
      
      <Grid item>
        <FilterComponent
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          filterValue={filterHabilitado}
          onFilterChange={handleFilterChange}
          filterOptions={filterOptions}
        />
      </Grid>
      
      <Grid item>
        <TableContainer component={Paper}>
          {isLoading && !initialDataLoaded ? (
            <Box 
              display="flex" 
              flexDirection="column" 
              alignItems="center" 
              justifyContent="center" 
              p={4}
            >
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Cargando clientes y organismos...
              </Typography>
            </Box>
          ) : filteredClientes.length > 0 ? (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '8%' }}>Id</TableCell>
                  <TableCell sx={{ width: '20%' }}>Cliente</TableCell>
                  <TableCell sx={{ width: '25%' }}>Organismos Emisores</TableCell>
                  <TableCell sx={{ width: '30%' }}>Detalle</TableCell>
                  <TableCell sx={{ width: '17%', textAlign: 'center' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClientes.map((cliente, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {cliente.id}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {cliente.nombre}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {renderOrganismosAsociados(cliente)}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      {renderTiposDocumento(cliente)}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Ver detalles de organismos y tipos">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setSelectedClienteDetail(cliente);
                              setDetailModalOpen(true);
                            }}
                            size="small"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Gestionar organismos y tipos">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              setClienteSeleccionado(cliente);
                              setOpenModal(true);
                            }}
                            size="small"
                          >
                            <SettingsIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Box p={3}>
              <Typography>
                No se encontraron clientes con los criterios de búsqueda actuales.
              </Typography>
            </Box>
          )}
        </TableContainer>
        
        <TablePagination
          component="div"
          count={clientes.filter((cliente) => {
            const matchesSearch = cliente.nombre
              .toLowerCase()
              .includes(searchTerm.toLowerCase());
            const matchesFilter =
              filterHabilitado === "todos" ||
              (filterHabilitado === "habilitado" && cliente.habilitado === true) ||
              (filterHabilitado === "deshabilitado" && cliente.habilitado === false);
            return matchesSearch && matchesFilter;
          }).length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </Grid>

      {/* Modal de gestión de relaciones */}
      <AgregarRelacionClienteOrganismoModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSubmit={handleGuardarRelaciones}
        cliente={clienteSeleccionado}
        organismosConTipos={organismosConTipos}
      />

      {/* Modal de detalle */}
      <DetailModal />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default GestionOrganismos;